import {
  createMutationStore,
  createPersistedStore,
  createQueryStore,
  useQueryClient,
} from '@ferreri/minato-react-utils';
import { useMemo } from 'react';
import { usePlanVersion } from '../plan/plan.store';
import { getOrder, updateOrder } from './order.client';
import { Order } from './order.types';

export const useOrder = (orderUuid: string) => {
  return createQueryStore({
    queryKey: ['order', orderUuid],
    queryFn: () => getOrder(orderUuid),
    enabled: !!orderUuid,
  });
};

export const useSaveCurrentPlanOrderId = () =>
  createPersistedStore<string>('current_plan_order_uuid');

export const useCurrentPlanOrderId = () => {
  const [order_uuid] = useSaveCurrentPlanOrderId();
  return order_uuid;
};

export const useCurrentOrder = () => {
  const {
    data: order,
    isSuccess: isSuccessOrder,
    isLoading: isLoadingOrder,
    isError: isErrorOrder,
    ...rest
  } = useOrder(useCurrentPlanOrderId());
  const { data: plan_version } = usePlanVersion(order?.plan_version_id);

  const isSuccess = isSuccessOrder;
  const isLoading = isLoadingOrder;
  const isError = isErrorOrder;

  return useMemo(() => {
    const formattedOrder = {
      ...order,
      plan_version,
    };

    return { ...rest, data: formattedOrder, isSuccess, isLoading, isError };
  }, [order, isError, isLoading, isSuccess, plan_version]);
};

export const useUpdateOrder = (config = null) => {
  const currentPlanOrderId = useCurrentPlanOrderId();
  const queryClient = useQueryClient();

  return createMutationStore({
    mutationFn: (order: Order) => updateOrder(order, currentPlanOrderId),
    onSuccess: () => queryClient.invalidateQueries(['order']),
    ...config,
  });
};
